import {FabricObject} from 'fabric';
import {ElementCanvas, SeatConfig, TableCanvas, TableShape} from '../types';
import {Resource} from 'crqlar-react-common/dist/models/resource/resource.model';
import {StoredElementV2} from './migration-utils';
import {
    getNormalizedShapeCanvas,
    getNormalizedTableCanvas,
    getNormalizedTextCanvas,
    getNormalizedWallPoints,
} from './resize-utils';

interface TableResourcePayload extends Partial<Resource> {
    capacity?: number;
    meta: Record<string, any> & {
        canvas: TableCanvas;
        shape: TableShape;
        seats?: SeatConfig;
    };
}

/**
 * Prepares a table resource payload from a Fabric object for create/update operations.
 * Serializes the current canvas state (position, size, angle) from the Fabric object.
 */
export const serializeTableResource = (obj: FabricObject, zIndex?: number): TableResourcePayload | null => {
    const item = obj.floorPlanItem;
    if (!item?.table) return null;

    const {resource, canvas, shape, seats} = item.table;
    const resourceMeta = (resource.meta as Record<string, any> | undefined) ?? {};

    // Save the baked geometry, not transient Fabric scale.
    const scaleX = obj.scaleX ?? 1;
    const scaleY = obj.scaleY ?? 1;

    const serializedCanvas: TableCanvas = {
        ...getNormalizedTableCanvas(canvas, shape, scaleX, scaleY),
        id: canvas.id ?? item.id,
        left: obj.left ?? canvas.left ?? 0,
        top: obj.top ?? canvas.top ?? 0,
        angle: obj.angle ?? canvas.angle ?? 0,
        zIndex: zIndex ?? canvas.zIndex ?? 0,
        scaleX: 1,
        scaleY: 1,
    };

    // Derive capacity from seats config
    const capacity = seats
        ? seats.edges.top + seats.edges.right + seats.edges.bottom + seats.edges.left
        : (resource as any).capacity ?? 4;

    return {
        ...resource,  // Spread all existing resource fields first
        capacity,
        meta: {
            ...resourceMeta,
            bookableExternally: resourceMeta.bookableExternally === true,
            canvas: serializedCanvas,
            shape,
            seats,
        },
    };
};

/**
 * Prepares an element payload from a Fabric object for storage.
 * Serializes the current canvas state (position, size, angle) from the Fabric object.
 */
export const serializeElement = (obj: FabricObject, zIndex?: number): StoredElementV2 | null => {
    const item = obj.floorPlanItem;
    if (!item?.element) return null;

    const {kind, canvas} = item.element;
    const scaleX = obj.scaleX ?? 1;
    const scaleY = obj.scaleY ?? 1;

    const serializedCanvas: ElementCanvas = {
        ...canvas,
        left: obj.left ?? canvas.left ?? 0,
        top: obj.top ?? canvas.top ?? 0,
        angle: obj.angle ?? canvas.angle ?? 0,
        zIndex: zIndex ?? canvas.zIndex ?? 0,
        scaleX: 1,
        scaleY: 1,
    };

    // Each element kind persists resize differently.
    if (kind === 'text') {
        Object.assign(serializedCanvas, getNormalizedTextCanvas(canvas as any, scaleX, scaleY));
    } else if (kind === 'wall') {
        (serializedCanvas as any).points = getNormalizedWallPoints(canvas as any, scaleX, scaleY);
    } else {
        Object.assign(serializedCanvas, getNormalizedShapeCanvas(canvas as any, (item.element as any).shape, scaleX, scaleY));
    }
    delete (serializedCanvas as any).id;

    return {
        id: item.id,
        kind,
        canvas: serializedCanvas,
        shape: kind === 'shape' ? (item.element as any).shape : undefined,
    };
};
